import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

import {startOfToday} from '../../modes/attendance/attendanceDateUtils'

type AttendanceMonthYearInputProps = {
  onSelect: (month: number, year: number) => void
  theme?: 'light' | 'dark'
  maxDate?: Date
}

const MONTH_IDS = [
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_JAN',
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_FEB',
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_MAR',
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_APR',
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_MAY',
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_JUN',
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_JUL',
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_AUG',
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_SEP',
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_OCT',
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_NOV',
  'CHATBOT.ATTENDANCE.DATE_RANGE_MONTH_DEC',
]

const AttendanceMonthYearInput: React.FC<AttendanceMonthYearInputProps> = ({
  onSelect,
  theme = 'light',
  maxDate,
}) => {
  const intl = useIntl()
  const today = maxDate ?? startOfToday()
  const [month, setMonth] = useState<number | null>(null)
  const [year, setYear] = useState<number | null>(null)

  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)'
  const selectStyle: React.CSSProperties = {
    flex: '1 1 0',
    minWidth: 0,
    fontSize: '0.8rem',
    padding: '0.35rem 0.4rem',
    borderRadius: 8,
    border: `1px solid ${borderColor}`,
    backgroundColor: theme === 'dark' ? '#1e1e2d' : '#fff',
    color: theme === 'dark' ? '#fff' : '#212529',
  }

  const years = useMemo(() => {
    const maxYear = today.getFullYear()
    const minYear = maxYear - 5
    const list: number[] = []
    for (let y = maxYear; y >= minYear; y -= 1) {
      list.push(y)
    }
    return list
  }, [today])

  const months = useMemo(() => {
    return MONTH_IDS.map((id, index) => ({
      value: index + 1,
      label: intl.formatMessage({id}),
    }))
  }, [intl])

  useEffect(() => {
    if (month == null || year == null) return
    const selected = new Date(year, month - 1, 1)
    if (selected > today) return
    onSelect(month, year)
  }, [month, onSelect, today, year])

  return (
    <div className='attendance-month-year-input mb-3'>
      <div className='d-flex gap-2 align-items-center'>
        <select
          aria-label={intl.formatMessage({id: 'CHATBOT.ATTENDANCE.MONTH_YEAR_MONTH'})}
          style={selectStyle}
          value={month ?? ''}
          onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : null)}
        >
          <option value=''>
            {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.MONTH_YEAR_MONTH'})}
          </option>
          {months.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <select
          aria-label={intl.formatMessage({id: 'CHATBOT.ATTENDANCE.MONTH_YEAR_YEAR'})}
          style={selectStyle}
          value={year ?? ''}
          onChange={(e) => setYear(e.target.value ? Number(e.target.value) : null)}
        >
          <option value=''>{intl.formatMessage({id: 'CHATBOT.ATTENDANCE.MONTH_YEAR_YEAR'})}</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

export default AttendanceMonthYearInput
